import React, { useEffect, useRef, useState } from "react";
import { SpeakerSimpleHigh, SpeakerSimpleSlash, X } from "@phosphor-icons/react";

export const OPEN_SPLASH_EVENT = "encore:open-splash";

// Once per browser session (per video) — repeat visits land instantly.
const splashKey = (src) => `encore-splash-seen:${src || "default"}`;
const seenThisSession = (src) => {
  try { return sessionStorage.getItem(splashKey(src)) === "1"; } catch { return false; }
};
const markSeen = (src) => {
  try { sessionStorage.setItem(splashKey(src), "1"); } catch { /* private mode */ }
};

/**
 * Full-screen intro splash that plays the explainer video on every visit to the
 * homepage. Tries to start with sound on; if the browser blocks unmuted
 * autoplay it falls back to muted (visitor can tap Unmute or the video).
 * Can be re-opened anytime via OPEN_SPLASH_EVENT (hero "Watch our story").
 */
/**
 * Full-screen intro splash that plays the explainer video on every visit.
 * Tries to start with sound on; if the browser blocks unmuted autoplay it
 * falls back to muted and unmutes on the visitor's first tap.
 * Pass `brand` ({ name, title, company, photoUrl }) to co-brand the splash
 * with a broker's identity on their personal landing pages.
 */
export default function VideoSplash({ brand = null, src = null, onEvent = null }) {
  const [show, setShow] = useState(() => !seenThisSession(src));
  const [closing, setClosing] = useState(false);
  const [muted, setMuted] = useState(false);
  const [soundBlocked, setSoundBlocked] = useState(false);
  const [photoOk, setPhotoOk] = useState(true);
  const ignoreClickRef = useRef(false);
  const playedRef = useRef(false);
  const videoRef = useRef(null);

  function emit(ev) {
    if (onEvent) {
      try { onEvent(ev); } catch { /* analytics must never break playback */ }
    }
  }

  useEffect(() => {
    if (show) markSeen(src);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function reopen() {
      setClosing(false);
      setMuted(false);
      setShow(true);
    }
    window.addEventListener(OPEN_SPLASH_EVENT, reopen);
    return () => window.removeEventListener(OPEN_SPLASH_EVENT, reopen);
  }, []);

  useEffect(() => {
    if (show) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted;
    const p = v.play();
    if (p && p.catch) {
      p.catch(() => {
        // Browser blocked sound-on autoplay — play muted and unmute on the
        // visitor's first tap anywhere on the splash.
        v.muted = true;
        setMuted(true);
        setSoundBlocked(true);
        v.play().catch(() => {});
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  function close() {
    setClosing(true);
    setTimeout(() => setShow(false), 450);
  }

  function toggleMute() {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    setSoundBlocked(false);
  }

  function handleRootPointerDown(e) {
    if (!soundBlocked) return;
    if (e.target.closest("button")) return;
    const v = videoRef.current;
    if (v) {
      v.muted = false;
      setMuted(false);
    }
    setSoundBlocked(false);
    ignoreClickRef.current = true;
  }

  function handleVideoClick() {
    if (ignoreClickRef.current) {
      ignoreClickRef.current = false;
      return;
    }
    toggleMute();
  }

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      onPointerDown={handleRootPointerDown}
      style={{
        zIndex: 9999,
        background: "#0E1525",
        height: "100dvh",
        opacity: closing ? 0 : 1,
        transition: "opacity 400ms ease",
      }}
      data-testid="video-splash"
    >
      <video
        ref={videoRef}
        autoPlay
        muted={muted}
        playsInline
        preload="auto"
        poster="/videos/encore-explainer-poster.jpg"
        onEnded={() => { emit("complete"); close(); }}
        onPlay={() => {
          if (!playedRef.current) {
            playedRef.current = true;
            emit("play");
          }
        }}
        onClick={handleVideoClick}
        className="w-full h-full"
        style={{ maxWidth: 1280, maxHeight: "100dvh", objectFit: "contain" }}
        data-testid="video-splash-player"
      >
        {src ? (
          <source src={src} onError={close} />
        ) : (
          <>
            {/* webm first — half the size of the mp4 for browsers that support it */}
            <source src="/videos/encore-explainer.webm" type="video/webm" />
            <source src="/videos/encore-explainer.mp4" type="video/mp4" onError={close} />
          </>
        )}
      </video>

      {soundBlocked && (
        <div className="absolute inset-0 flex items-end sm:items-center justify-center pointer-events-none" style={{ paddingBottom: "18vh" }}>
          <span
            className="flex items-center gap-2 text-sm px-5 py-3 font-medium animate-pulse"
            style={{
              background: "rgba(255,255,255,0.95)", color: "#0E1525",
              borderRadius: 999, boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
            }}
            data-testid="video-splash-tap-for-sound"
          >
            <SpeakerSimpleHigh size={18} weight="duotone" /> Tap anywhere for sound
          </span>
        </div>
      )}

      {brand && (
        <div
          className="absolute flex items-center gap-3"
          style={{
            top: "calc(env(safe-area-inset-top, 0px) + 14px)",
            left: "calc(env(safe-area-inset-left, 0px) + 16px)",
            maxWidth: "55vw",
          }}
          data-testid="video-splash-brand"
        >
          {brand.photoUrl && photoOk ? (
            <img
              src={brand.photoUrl}
              alt={brand.name}
              onError={() => setPhotoOk(false)}
              style={{
                width: 46, height: 46, borderRadius: 999, objectFit: "cover",
                border: "2px solid rgba(255,255,255,0.5)", flexShrink: 0,
              }}
            />
          ) : (
            <span
              className="inline-flex items-center justify-center text-sm font-semibold"
              style={{
                width: 46, height: 46, borderRadius: 999, flexShrink: 0,
                background: "rgba(255,255,255,0.15)", color: "#FFFFFF",
                border: "2px solid rgba(255,255,255,0.5)",
              }}
            >
              {(brand.name || "E").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
            </span>
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate" style={{ color: "#FFFFFF" }} data-testid="video-splash-brand-name">
              {brand.name}
            </p>
            <p className="text-xs truncate" style={{ color: "rgba(255,255,255,0.65)" }}>
              {brand.title || brand.company || "Your loan officer"}
            </p>
          </div>
        </div>
      )}

      <div
        className="absolute flex items-center gap-2"
        style={{
          top: "calc(env(safe-area-inset-top, 0px) + 14px)",
          right: "calc(env(safe-area-inset-right, 0px) + 14px)",
        }}
      >
        <button
          type="button"
          onClick={toggleMute}
          className="flex items-center gap-1.5 text-xs px-3 py-2 sm:px-4 sm:py-2.5 font-medium"
          style={{
            background: "rgba(255,255,255,0.12)", color: "#FFFFFF",
            border: "1px solid rgba(255,255,255,0.3)", borderRadius: 999,
            backdropFilter: "blur(8px)", minHeight: 40,
          }}
          data-testid="video-splash-mute"
        >
          {muted ? <SpeakerSimpleSlash size={15} /> : <SpeakerSimpleHigh size={15} />}
          <span className="hidden sm:inline">{muted ? "Unmute" : "Mute"}</span>
        </button>
        <button
          type="button"
          onClick={() => { emit("skip"); close(); }}
          className="flex items-center gap-1.5 text-xs px-3 py-2 sm:px-4 sm:py-2.5 font-medium"
          style={{
            background: "#FFFFFF", color: "#0E1525",
            border: "1px solid #FFFFFF", borderRadius: 999, minHeight: 40,
          }}
          data-testid="video-splash-skip"
        >
          Skip intro <X size={14} />
        </button>
      </div>

      <p
        className="hidden sm:block absolute bottom-5 left-1/2 -translate-x-1/2 text-xs tracking-widest uppercase text-center"
        style={{ color: "rgba(255,255,255,0.55)", letterSpacing: "0.2em", maxWidth: "90vw" }}
      >
        {brand
          ? `${brand.name}${brand.company && brand.company !== brand.name ? ` · ${brand.company}` : ""} · with Encore Commercial Capital`
          : "Encore Commercial Capital Corporation"}
      </p>
    </div>
  );
}
